import React from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { AccountType, AccountTypeOptionsDTO } from '../../../../../../api/useAccountsOptionsQuery';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { accountPortfolioDataToForm, FormFields } from '../../AccountForm.meta';
import { Radio } from '../../../../../../../../CommonComponents/OPL/Radio/Radio';
import { TaxStatusDTO, BalanceUnitDTO } from '../../../../../../api/useAccountsQuery';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { AccountEarmark } from '../AccountEarmark/AccountEarmark';
import { AssetBucketing } from '../AssetBucketing/AssetBucketing';
import { useAccountFormContext } from '../../context/useAccountFormContext';

export const TaxStatus = () => {
  const formBag = useFormContext<FormFields>();

  const { options } = useAccountFormContext();

  const {
    AccountTypeOptions: accountsTypeOptions,
    ManagementStyles,
    AssetBucketingByManagementStyle,
    DefaultAssetBucketingIndicesByTaxStatus,
  } = options;

  const { watch } = formBag;

  const [accountType, taxStatus, unit, orionManagementStyleId] = watch([
    'bankAccount.AccountType',
    'bankAccount.TaxStatus',
    'bankAccount.Balances.Unit',
    'bankAccount.OrionManagementStyleId',
  ]);

  const accountTypeOptions = accountsTypeOptions.find(
    option => option.AccountType === accountType,
  ) as AccountTypeOptionsDTO;

  const balanceTaxStatusOptions = accountTypeOptions.BalanceTaxStatuses.map(opt => ({
    label: opt.Label,
    value: opt.Value,
  }));

  if (balanceTaxStatusOptions.length === 0) {
    return <AccountEarmark />;
  }

  if (accountType === null) {
    throw new Error('Account type not found');
  }

  const getDefaultAssetBucketIndex = (key: string) => {
    if (!AssetBucketingByManagementStyle) {
      const defaultByAccountType =
        DefaultAssetBucketingIndicesByTaxStatus[AccountType[accountType]];

      return defaultByAccountType[key] ?? null;
    }

    const selectedManagementStyle = ManagementStyles.find(
      option => option.id === orionManagementStyleId,
    );

    return selectedManagementStyle?.defaultBucket ?? null;
  };

  return (
    <>
      <Controller
        control={formBag.control}
        name="bankAccount.TaxStatus"
        render={({ field: { value, onChange, ref } }) => (
          <Select
            innerRef={ref}
            value={balanceTaxStatusOptions.find(option => option.value === value) || null}
            options={balanceTaxStatusOptions}
            onChange={opt => {
              if (!opt) {
                onChange(null);
                formBag.resetField('accountPortfolio.assetAllocationList');
                return;
              }

              if (accountType) {
                const accountPortfolio = accountPortfolioDataToForm({
                  accountType,
                  accountOptions: options,
                });

                const defaultAssetAllocationValues = formBag.getValues(
                  'accountPortfolio.assetAllocationList',
                );

                if (accountPortfolio && !defaultAssetAllocationValues) {
                  formBag.setValue(
                    'accountPortfolio.assetAllocationList',
                    accountPortfolio.assetAllocationList,
                  );
                }

                if (
                  accountPortfolio &&
                  defaultAssetAllocationValues &&
                  opt.value === TaxStatusDTO.Mix
                ) {
                  const nullTaxStatusAllocation = defaultAssetAllocationValues.find(
                    allocation => allocation.taxStatus === null,
                  );

                  const updatePercentage = accountPortfolio.assetAllocationList.map(allocation => {
                    if (nullTaxStatusAllocation?.assetAllocation) {
                      return {
                        ...allocation,
                        assetAllocation: nullTaxStatusAllocation.assetAllocation,
                      };
                    }

                    return allocation;
                  });

                  formBag.setValue('accountPortfolio.assetAllocationList', updatePercentage);
                }

                balanceTaxStatusOptions
                  .filter(option =>
                    opt.value === TaxStatusDTO.Mix
                      ? option.value !== TaxStatusDTO.Mix
                      : option.value === opt.value,
                  )
                  .forEach(option => {
                    const key = TaxStatusDTO[option.value];

                    const defaultAssetBucket = getDefaultAssetBucketIndex(key);

                    const formPath = `bankAccount.Balances.${key}.AssetBucketIndex` as Path<
                      FormFields
                    >;

                    formBag.setValue(formPath, defaultAssetBucket);
                  });
              }

              onChange(opt.value);
            }}
            label="Tax Status"
            placeholder="Tax Status"
            isPortal
            isClearable
          />
        )}
      />

      {taxStatus === TaxStatusDTO.Mix && (
        <>
          <Box display="flex" css="gap: 4px">
            <Controller
              name="bankAccount.Balances.Unit"
              control={formBag.control}
              render={({ field: { value, name, onChange } }) => (
                <>
                  {[
                    { label: '$', value: BalanceUnitDTO.Currency },
                    { label: '%', value: BalanceUnitDTO.Percentage },
                  ].map(option => (
                    <Radio
                      key={option.value}
                      name={name}
                      checked={value === option.value}
                      onChange={() => {
                        onChange(option.value);

                        formBag.setValue('bankAccount.Balances.Roth.NonSpecificPositionsValue', '');
                        formBag.setValue(
                          'bankAccount.Balances.AfterTax.NonSpecificPositionsValue',
                          '',
                        );
                        formBag.setValue(
                          'bankAccount.Balances.PreTax.NonSpecificPositionsValue',
                          '',
                        );
                      }}
                      mr="4"
                    >
                      {option.label}
                    </Radio>
                  ))}
                </>
              )}
            />
          </Box>
        </>
      )}

      {taxStatus !== null && (
        <>
          {!!balanceTaxStatusOptions.find(option => option.value === TaxStatusDTO.Roth) && (
            <>
              {[TaxStatusDTO.Mix].includes(taxStatus) && (
                <Controller
                  control={formBag.control}
                  name="bankAccount.Balances.Roth.NonSpecificPositionsValue"
                  render={({ field: { onChange, ...rest } }) => (
                    <InputNumberMask
                      {...rest}
                      leftIcon={unit === BalanceUnitDTO.Currency ? 'fa fa-dollar-sign' : undefined}
                      rightIcon={unit === BalanceUnitDTO.Percentage ? 'far fa-percent' : undefined}
                      label="Roth Balance"
                      onValueChange={({ floatValue }) => {
                        onChange(floatValue ?? null);
                      }}
                    />
                  )}
                />
              )}

              {[TaxStatusDTO.Mix, TaxStatusDTO.Roth].includes(taxStatus) && (
                <>
                  <AccountEarmark
                    formKey="bankAccount.Balances.Roth.Earmarks"
                    taxStatus={TaxStatusDTO.Roth}
                  />

                  <AssetBucketing
                    formKey="bankAccount.Balances.Roth.AssetBucketIndex"
                    taxStatus={TaxStatusDTO.Roth}
                  />
                </>
              )}
            </>
          )}

          {balanceTaxStatusOptions.find(option => option.value === TaxStatusDTO.PreTax) && (
            <>
              {[TaxStatusDTO.Mix].includes(taxStatus) && (
                <Controller
                  control={formBag.control}
                  name="bankAccount.Balances.PreTax.NonSpecificPositionsValue"
                  render={({ field: { onChange, ...rest } }) => (
                    <InputNumberMask
                      {...rest}
                      leftIcon={unit === BalanceUnitDTO.Currency ? 'fa fa-dollar-sign' : undefined}
                      rightIcon={unit === BalanceUnitDTO.Percentage ? 'far fa-percent' : undefined}
                      label="Pre-tax Balance"
                      onValueChange={({ floatValue }) => {
                        onChange(floatValue ?? null);
                      }}
                    />
                  )}
                />
              )}

              {[TaxStatusDTO.Mix, TaxStatusDTO.PreTax].includes(taxStatus) && (
                <>
                  <AccountEarmark
                    formKey="bankAccount.Balances.PreTax.Earmarks"
                    taxStatus={TaxStatusDTO.PreTax}
                  />

                  <AssetBucketing
                    formKey="bankAccount.Balances.PreTax.AssetBucketIndex"
                    taxStatus={TaxStatusDTO.PreTax}
                  />
                </>
              )}
            </>
          )}

          {balanceTaxStatusOptions.find(option => option.value === TaxStatusDTO.AfterTax) && (
            <>
              {[TaxStatusDTO.Mix].includes(taxStatus) && (
                <Controller
                  control={formBag.control}
                  name="bankAccount.Balances.AfterTax.NonSpecificPositionsValue"
                  render={({ field: { onChange, ...rest } }) => (
                    <InputNumberMask
                      {...rest}
                      leftIcon={unit === BalanceUnitDTO.Currency ? 'fa fa-dollar-sign' : undefined}
                      rightIcon={unit === BalanceUnitDTO.Percentage ? 'far fa-percent' : undefined}
                      label="After-Tax Balance"
                      onValueChange={({ floatValue }) => {
                        onChange(floatValue ?? null);
                      }}
                    />
                  )}
                />
              )}

              {[TaxStatusDTO.Mix, TaxStatusDTO.AfterTax].includes(taxStatus) && (
                <>
                  <AccountEarmark
                    formKey="bankAccount.Balances.AfterTax.Earmarks"
                    taxStatus={TaxStatusDTO.AfterTax}
                  />

                  <AssetBucketing
                    formKey="bankAccount.Balances.AfterTax.AssetBucketIndex"
                    taxStatus={TaxStatusDTO.AfterTax}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
